import type { UseCaseSection, UseCaseSectionType } from './useCaseQueries'

function hasText(value?: string | null) {
  return Boolean(value?.trim())
}

type Stat = { value?: string; label?: string; detail?: string }

function hasStats(stats?: Stat[]) {
  return Boolean(stats?.some((s) => hasText(s.value) || hasText(s.label)))
}

function hasTableRows(table?: { rows?: { cells?: { content?: string }[] }[] }) {
  if (!table?.rows?.length) return false
  return table.rows.some((row) => row.cells?.some((cell) => hasText(cell.content)))
}

export function hasUcHero(section: UseCaseSection) {
  if (section._type !== 'ucHero') return false
  return (
    hasText(section.headingMain) ||
    hasText(section.headingAccent) ||
    hasText(section.body)
  )
}

export function hasUcProblemStats(section: UseCaseSection) {
  if (section._type !== 'ucProblemStats') return false
  return (
    hasText(section.heading) ||
    hasStats(section.stats) ||
    hasText(section.quote)
  )
}

export function hasUcBenefitStats(section: UseCaseSection) {
  if (section._type !== 'ucBenefitStats') return false
  return hasStats(section.cells)
}

export function hasUcWorkflowStep(section: UseCaseSection) {
  if (section._type !== 'ucWorkflowStep') return false
  return (
    hasText(section.heading) ||
    hasText(section.body) ||
    hasText(section.kicker)
  )
}

export function hasUcFormatGrid(section: UseCaseSection) {
  if (section._type !== 'ucFormatGrid') return false
  return Boolean(section.cards?.some((c) => hasText(c.title)))
}

export function hasUcFeatures(section: UseCaseSection) {
  if (section._type !== 'ucFeatures') return false
  return Boolean(section.features?.some((f) => hasText(f.title)))
}

export function hasUcComparisonTable(section: UseCaseSection) {
  if (section._type !== 'ucComparisonTable') return false
  return hasTableRows(section.table)
}

export function hasUcTestimonial(section: UseCaseSection) {
  if (section._type !== 'ucTestimonial') return false
  return hasText(section.quote)
}

export function hasUcPricingCallout(section: UseCaseSection) {
  if (section._type !== 'ucPricingCallout') return false
  return (
    hasText(section.heading) ||
    hasText(section.body) ||
    hasText(section.ctaLabel)
  )
}

export function hasUcFaq(section: UseCaseSection) {
  if (section._type !== 'ucFaq') return false
  return Boolean(section.items?.length)
}

export function hasUcCtaBanner(section: UseCaseSection) {
  if (section._type !== 'ucCtaBanner') return false
  return (
    hasText(section.headingMain) ||
    hasText(section.headingAccent) ||
    hasText(section.primaryCtaLabel)
  )
}

const contentChecks: Record<UseCaseSectionType, (s: UseCaseSection) => boolean> = {
  ucHero: hasUcHero,
  ucProblemStats: hasUcProblemStats,
  ucBenefitStats: hasUcBenefitStats,
  ucWorkflowStep: hasUcWorkflowStep,
  ucFormatGrid: hasUcFormatGrid,
  ucFeatures: hasUcFeatures,
  ucComparisonTable: hasUcComparisonTable,
  ucTestimonial: hasUcTestimonial,
  ucPricingCallout: hasUcPricingCallout,
  ucFaq: hasUcFaq,
  ucCtaBanner: hasUcCtaBanner,
}

export function sectionHasContent(section: UseCaseSection) {
  const check = contentChecks[section._type]
  return check ? check(section) : false
}

export function hasAnyUseCaseSection(sections: UseCaseSection[]) {
  return sections.some(sectionHasContent)
}

type BandTone = 'light' | 'dark'

const fixedTones: Partial<Record<UseCaseSectionType, BandTone>> = {
  ucHero: 'dark',
  ucProblemStats: 'light',
  ucBenefitStats: 'dark',
  ucComparisonTable: 'light',
  ucTestimonial: 'dark',
  ucPricingCallout: 'light',
  ucCtaBanner: 'dark',
}

const autoToneTypes = new Set<UseCaseSectionType>([
  'ucWorkflowStep',
  'ucFormatGrid',
  'ucFeatures',
  'ucFaq',
])

function getCtaBannerIndex(sections: UseCaseSection[]) {
  return sections.findIndex((s) => s._type === 'ucCtaBanner' && hasUcCtaBanner(s))
}

export function resolveSectionBands(sections: UseCaseSection[]): Map<string, BandTone> {
  const bands = new Map<string, BandTone>()
  const ctaIndex = getCtaBannerIndex(sections)
  const sectionBeforeCta =
    ctaIndex > 0
      ? sections
          .slice(0, ctaIndex)
          .filter(sectionHasContent)
          .at(-1)
      : undefined

  let lastTone: BandTone = 'dark'

  for (const section of sections) {
    if (!sectionHasContent(section)) continue

    const override = section.toneOverride
    if (override === 'dark' || override === 'light') {
      bands.set(section._key, override)
      lastTone = override
      continue
    }

    const fixed = fixedTones[section._type]
    if (fixed) {
      let tone = fixed
      if (
        section._type === 'ucFaq' &&
        sectionBeforeCta?._key === section._key
      ) {
        tone = 'light'
      }
      bands.set(section._key, tone)
      lastTone = tone
      continue
    }

    if (autoToneTypes.has(section._type)) {
      const nextTone: BandTone = lastTone === 'dark' ? 'light' : 'dark'
      let tone = nextTone
      if (
        section._type === 'ucFaq' &&
        sectionBeforeCta?._key === section._key
      ) {
        tone = 'light'
      }
      bands.set(section._key, tone)
      lastTone = tone
    }
  }

  return bands
}

export function findFaqSection(sections: UseCaseSection[]) {
  return sections.find((s) => s._type === 'ucFaq' && hasUcFaq(s))
}
